import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { ErrorState } from '@/ui/ErrorState';
import { Card } from '@/ui/base/card';
import { Switch } from '@/ui/base/switch';
import { Badge } from '@/ui/base/badge';

type TipoAcaoAprovacao =
  | 'SUBSTITUICAO_VEICULO'
  | 'SUBSTITUICAO_CONDUTOR'
  | 'SUBSTITUICAO_MONITOR'
  | 'ALTERACAO_DRE'
  | 'CORRECAO_DADOS'
  | 'INCLUSAO_VEICULO'
  | 'REVISAO_BANCADA'
  | 'ALTERACAO_BANCADA'
  | 'TROCA_MODALIDADE'
  | 'ATUALIZACAO_ACESSIBILIDADE'
  | 'ALTERACAO_ENDERECO'
  | 'ALTERACAO_CONTA_BANCARIA'
  | 'INCLUSAO_COLABORADOR'
  | 'OUTROS';

const ROTULO_TIPO_ACAO: Record<TipoAcaoAprovacao, string> = {
  SUBSTITUICAO_VEICULO: 'Substituição de veículo',
  SUBSTITUICAO_CONDUTOR: 'Substituição de condutor',
  SUBSTITUICAO_MONITOR: 'Substituição de monitor',
  ALTERACAO_DRE: 'Alteração de DRE',
  CORRECAO_DADOS: 'Correção de dados',
  INCLUSAO_VEICULO: 'Inclusão de veículo',
  REVISAO_BANCADA: 'Revisão de bancada',
  ALTERACAO_BANCADA: 'Alteração de bancada',
  TROCA_MODALIDADE: 'Troca de modalidade',
  ATUALIZACAO_ACESSIBILIDADE: 'Atualização de acessibilidade',
  ALTERACAO_ENDERECO: 'Alteração de endereço',
  ALTERACAO_CONTA_BANCARIA: 'Alteração de conta bancária',
  INCLUSAO_COLABORADOR: 'Inclusão de colaborador',
  OUTROS: 'Outras ações',
};

const DESCRICAO_TIPO_ACAO: Record<TipoAcaoAprovacao, string> = {
  SUBSTITUICAO_VEICULO: 'Troca do veículo vinculado a um credenciado.',
  SUBSTITUICAO_CONDUTOR: 'Troca do condutor vinculado a um credenciado.',
  SUBSTITUICAO_MONITOR: 'Troca do monitor vinculado a um credenciado.',
  ALTERACAO_DRE: 'Mudança da diretoria regional de ensino do credenciado.',
  CORRECAO_DADOS: 'Correção de dados cadastrais já homologados.',
  INCLUSAO_VEICULO: 'Inclusão de novo veículo na frota do credenciado.',
  REVISAO_BANCADA: 'Revisão da bancada de assentos declarada do veículo.',
  ALTERACAO_BANCADA: 'Alteração da bancada de assentos do veículo.',
  TROCA_MODALIDADE: 'Troca da modalidade de transporte contratada.',
  ATUALIZACAO_ACESSIBILIDADE: 'Atualização dos recursos de acessibilidade do veículo.',
  ALTERACAO_ENDERECO: 'Mudança do endereço declarado do credenciado.',
  ALTERACAO_CONTA_BANCARIA: 'Alteração dos dados bancários de pagamento.',
  INCLUSAO_COLABORADOR: 'Inclusão de novo colaborador do credenciado.',
  OUTROS: 'Ações que não se encaixam nas categorias acima.',
};

interface ParametroAprovacao {
  tipoAcao: TipoAcaoAprovacao;
  requerValidacaoDtegDefault: boolean;
  bloqueadoDteg: boolean;
}

const PARAMETROS_MOCK: ParametroAprovacao[] = (
  Object.keys(ROTULO_TIPO_ACAO) as TipoAcaoAprovacao[]
).map((tipoAcao, i) => ({
  tipoAcao,
  requerValidacaoDtegDefault: i % 3 !== 0,
  bloqueadoDteg: tipoAcao === 'ALTERACAO_CONTA_BANCARIA' || tipoAcao === 'CORRECAO_DADOS',
}));

/**
 * `/admin/parametros/aprovacoes` — ONDA-CA2, item ADM-089 do laudo de comparação QA.
 *
 * NÃO confundir com `AprovacaoParametroPage.tsx` deste mesmo diretório: aquela
 * é a jornada J-11 (aprovar uma VERSÃO de parâmetro proposta). Esta tela
 * configura, por TIPO DE AÇÃO do credenciado, se a alteração exige validação
 * DTEG e se está bloqueada para o próprio credenciado alterar — conceito
 * diferente, mesma área. Sem API própria ainda: estado local mockado (ver
 * docs/ALVOS.md, alvo ONDA-CA2).
 */
export function ParametrosAprovacaoDtegPage() {
  const [parametros, setParametros] = useState<ParametroAprovacao[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    const tempo = window.setTimeout(() => {
      if (cancelado) return;
      try {
        setParametros(PARAMETROS_MOCK.map((p) => ({ ...p })));
      } catch {
        setErro('Não foi possível carregar os parâmetros de aprovação.');
      } finally {
        setCarregando(false);
      }
    }, 200);
    return () => {
      cancelado = true;
      window.clearTimeout(tempo);
    };
  }, []);

  function alternar(
    tipoAcao: TipoAcaoAprovacao,
    campo: 'requerValidacaoDtegDefault' | 'bloqueadoDteg',
    valor: boolean,
  ) {
    setParametros((atual) =>
      (atual ?? []).map((p) => (p.tipoAcao === tipoAcao ? { ...p, [campo]: valor } : p)),
    );
    toast.success('Parâmetro atualizado (dado mockado — ainda não persiste em API real).');
  }

  if (erro) {
    return (
      <ErrorState
        variant="load-failed"
        description={erro}
        primaryAction={{ label: 'Tentar novamente', onClick: () => window.location.reload() }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Parâmetros de aprovação"
        description="Configure quais ações exigem validação DTEG e quais são bloqueadas para o credenciado alterar."
      />

      {carregando ? (
        <Spinner />
      ) : (
        <div className="space-y-3">
          {(parametros ?? []).map((p) => (
            <Card key={p.tipoAcao} className="p-4">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{ROTULO_TIPO_ACAO[p.tipoAcao]}</span>
                    {p.bloqueadoDteg && (
                      <Badge variant="outline" className="text-xs">
                        <Lock className="mr-1 h-3 w-3" /> Bloqueado
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{DESCRICAO_TIPO_ACAO[p.tipoAcao]}</p>
                </div>
                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={p.requerValidacaoDtegDefault}
                      onCheckedChange={(v) => alternar(p.tipoAcao, 'requerValidacaoDtegDefault', v)}
                      aria-label={`Requer DTEG para ${ROTULO_TIPO_ACAO[p.tipoAcao]}`}
                    />
                    <span className="text-sm">Requer DTEG</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={p.bloqueadoDteg}
                      onCheckedChange={(v) => alternar(p.tipoAcao, 'bloqueadoDteg', v)}
                      aria-label={`Bloquear alteração de ${ROTULO_TIPO_ACAO[p.tipoAcao]}`}
                    />
                    <span className="text-sm">Bloquear alteração</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default ParametrosAprovacaoDtegPage;
